import api from './api.service';

export interface User {
  id: string;
  company_id?: string;
  parent_company_id?: string;
  company_ids?: string[];
  memberships?: { company_id: string; name: string; code: string; is_parent: boolean }[];
  username: string;
  email: string;
  first_name: string;
  last_name?: string;
  phone?: string;
  avatar_url?: string;
  designation?: string;
  default_department_id?: string | null;
  signature_image_url?: string;
  is_active: boolean;
  is_super_admin: boolean;
  password_must_change?: boolean;
  failed_login_attempts?: number;
  locked_until?: string | null;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

/** What a user still holds in approval routing (returned on deactivate / employee exit). */
export interface ApprovalResponsibilitySummary {
  pending_approvals: number;
  process_owner_departments: string[];
  workflow_steps: number;
  has_any: boolean;
}

export interface UserRoleAssignment {
  id: string;
  role_id: string;
  role_name: string;
  role_code: string;
  permissions: { id: string; code: string; name: string; module: string }[];
}

function unwrap<T>(response: any): T {
  const d = response?.data?.data ?? response?.data;
  return d as T;
}

class UserService {
  private baseURL = '/users';

  async getAll(filters?: {
    company_id?: string;
    is_active?: boolean;
    search?: string;
    role_codes?: string[];
    include_user_id?: string;
  }): Promise<User[]> {
    const params: Record<string, string | boolean> = {};
    if (filters?.company_id) params.company_id = filters.company_id;
    if (filters?.is_active !== undefined) params.is_active = filters.is_active;
    if (filters?.search) params.search = filters.search;
    if (filters?.role_codes?.length) params.role_codes = filters.role_codes.join(',');
    if (filters?.include_user_id) params.include_user_id = filters.include_user_id;
    const response = await api.get(this.baseURL, { params });
    const data = unwrap<User[] | undefined>(response);
    return Array.isArray(data) ? data : [];
  }

  async getById(id: string): Promise<User> {
    const response = await api.get(`${this.baseURL}/${id}`);
    return unwrap<User>(response);
  }

  async create(data: {
    username: string;
    email: string;
    first_name: string;
    last_name?: string;
    designation?: string;
    default_department_id?: string | null;
    phone?: string;
    password?: string;
    send_invite?: boolean;
    password_must_change?: boolean;
    company_id?: string;
    company_ids?: string[];
    role_ids?: string[];
  }): Promise<{
    user: User;
    inviteEmailSent?: boolean;
    passwordSetupLink?: string;
  }> {
    const response = await api.post(this.baseURL, data);
    return unwrap(response);
  }

  async update(
    id: string,
    body: Partial<User> & { company_ids?: string[] }
  ): Promise<User> {
    const response = await api.put(`${this.baseURL}/${id}`, body);
    return unwrap<User>(response);
  }

  async delete(id: string): Promise<void> {
    await api.delete(`${this.baseURL}/${id}`);
  }

  async activate(id: string): Promise<User> {
    const response = await api.post(`${this.baseURL}/${id}/activate`);
    return unwrap<User>(response);
  }

  async deactivate(id: string): Promise<User & { responsibility_summary?: ApprovalResponsibilitySummary | null }> {
    const response = await api.post(`${this.baseURL}/${id}/deactivate`);
    return unwrap<User & { responsibility_summary?: ApprovalResponsibilitySummary | null }>(response);
  }

  async unlock(id: string): Promise<User> {
    const response = await api.post(`${this.baseURL}/${id}/unlock`);
    return unwrap<User>(response);
  }

  async assignRoles(id: string, role_ids: string[]): Promise<void> {
    await api.post(`${this.baseURL}/${id}/roles`, { role_ids });
  }

  async getUserRoles(id: string): Promise<UserRoleAssignment[]> {
    const response = await api.get(`${this.baseURL}/${id}/roles`);
    const data = unwrap<UserRoleAssignment[] | undefined>(response);
    return Array.isArray(data) ? data : [];
  }

  async getEffectivePermissions(id: string): Promise<string[]> {
    const response = await api.get(`${this.baseURL}/${id}/effective-permissions`);
    const data = unwrap<{ permissions: string[] }>(response);
    return data?.permissions ?? [];
  }

  async adminResetPassword(
    id: string,
    new_password: string,
    password_must_change?: boolean
  ): Promise<User> {
    const response = await api.post(`${this.baseURL}/${id}/reset-password`, {
      new_password,
      password_must_change: password_must_change !== false,
    });
    return unwrap<User>(response);
  }

  async uploadAvatar(id: string, file: File): Promise<{ avatar_url: string; user: User }> {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post(`${this.baseURL}/${id}/avatar`, fd);
    return unwrap<{ avatar_url: string; user: User }>(response);
  }

  async uploadSignature(id: string, file: File): Promise<{ signature_image_url: string; user: User }> {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post(`${this.baseURL}/${id}/signature`, fd);
    return unwrap<{ signature_image_url: string; user: User }>(response);
  }

  async removeAvatar(id: string): Promise<void> {
    await api.delete(`${this.baseURL}/${id}/avatar`);
  }

  async removeSignature(id: string): Promise<void> {
    await api.delete(`${this.baseURL}/${id}/signature`);
  }
}

export const userService = new UserService();
export default userService;
